import { useState } from "react";
import { Trash2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface DeleteProjectData {
  project_id: string;
  project_name: string;
}

interface DeleteProjectWidgetProps {
  data: DeleteProjectData;
  onAction?: (actionId: string, context?: Record<string, unknown>) => void;
}

export function DeleteProjectWidget({ data, onAction }: DeleteProjectWidgetProps) {
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (dismissed) return null;

  async function handleDelete() {
    setLoading(true);
    setError(null);
    try {
      await api.projects.delete(data.project_id);
      // Navigate home — use window.location to force a clean reload
      window.location.href = "/";
    } catch (err) {
      console.error("[DeleteProjectWidget] delete failed:", err);
      setError(err instanceof Error ? err.message : "Failed to delete project");
      setLoading(false);
    }
  }

  return (
    <Card variant="error" padding="default" className="max-w-md animate-scale-in">
      <div className="flex gap-3">
        <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-daydra-error/10 shrink-0">
          <Trash2 size={16} className="text-daydra-error" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-daydra-text">
            Delete {data.project_name}?
          </p>
          <p className="text-xs text-daydra-dim mt-1">
            This will archive the project. It won't delete your GitHub repository.
          </p>
          {error && (
            <p className="text-xs text-daydra-error mt-1">{error}</p>
          )}
          <div className="flex gap-2 mt-3">
            <Button
              variant="danger"
              size="sm"
              disabled={loading}
              onClick={handleDelete}
            >
              {loading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                "Delete"
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={loading}
              onClick={() => setDismissed(true)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
